/**
 * Autosave (decision 29: about 500 ms after typing pauses; tenet 3: never hold work hostage). Table A in the part 09
 * log; one test per row. The editor reports each change; the autosave saves the latest text once typing pauses,
 * never runs two saves at once, keeps the text unsaved after a failure and tries again, and saves on demand before
 * anything that needs the stored text to be current (a split, a trash, closing the chapter). Time and timers are
 * injected, so it is pure logic.
 */

/** Quiet time after the last change before a save starts (decision 29). */
export const QUIET_MS = 500;
/** Longest a change waits for a save while typing never pauses (A3). */
export const MAX_WAIT_MS = 5_000;
/** Wait before trying a failed save again, when no typing comes first (A7). */
export const RETRY_MS = 5_000;

/**
 * What one save did. `saved`: stored as the chapter; `copied`: stored as a conflict copy because another tab holds
 * the edit lock (part 06, S3, S4); `failed`: not stored. Part 06's `SaveResult` is one of these.
 */
export type AutosaveResult = { readonly kind: 'saved' } | { readonly kind: 'copied' } | { readonly kind: 'failed' };

/**
 * Where the writer's text stands. `saved`: every change is stored (as the chapter or a copy); `unsaved`: a save is
 * due; `saving`: a save is running; `failed`: the last save failed and a retry is due; `closed`: the autosave was
 * closed after its last save.
 */
export type AutosaveStatus = 'saved' | 'unsaved' | 'saving' | 'failed' | 'closed';

/** Runs `run` after `ms` milliseconds; the returned function cancels it. */
export type Schedule = (ms: number, run: () => void) => () => void;

/** What an autosave needs; everything with an effect is injected. */
export interface AutosaveOptions {
  /** The editor's text now. Read once per save, never per change. */
  readonly read: () => string;
  /** Stores the text. Should never reject; a rejection counts as `failed`. */
  readonly save: (text: string) => Promise<AutosaveResult>;
  readonly schedule: Schedule;
  /** Milliseconds, from the clock `schedule` runs on. */
  readonly now: () => number;
  /** Called on every change of status. */
  readonly onStatus?: ((status: AutosaveStatus) => void) | undefined;
}

const FAILED: AutosaveResult = { kind: 'failed' };

/** Table A: saves an editor's text after typing pauses, one save at a time. */
export class Autosave {
  /** Whether the text has changes no save has read yet. */
  private dirty = false;
  /** When the next save is due at the latest, while dirty (A3, A7). */
  private deadline = 0;
  private cancelTimer: (() => void) | null = null;
  private running: Promise<AutosaveResult> | null = null;
  private last: AutosaveResult = { kind: 'saved' };
  private closing: Promise<AutosaveResult> | null = null;
  private current: AutosaveStatus = 'saved';
  private readonly options: AutosaveOptions;

  constructor(options: AutosaveOptions) {
    this.options = options;
  }

  /** The status now. */
  get status(): AutosaveStatus {
    return this.current;
  }

  /** The text changed (A1–A3, A6). Ignored once closing (A13). */
  changed(): void {
    if (this.closing !== null) return;
    const now = this.options.now();
    if (!this.dirty) {
      this.dirty = true;
      this.deadline = now + MAX_WAIT_MS;
    }
    this.arm(Math.min(QUIET_MS, Math.max(0, this.deadline - now)));
    // A failure stays shown until a save succeeds.
    if (this.running === null && this.current !== 'failed') this.setStatus('unsaved');
  }

  /**
   * Saves now whatever is unsaved, after the save running, and resolves with the result of the save that covers the
   * text as it is at the call (A9–A12); with nothing unsaved, the last save's result.
   */
  flush(): Promise<AutosaveResult> {
    this.disarm();
    return this.drain();
  }

  /** Flushes, then takes no more changes and leaves no timer (A13, A14). Calling it again gives the same promise. */
  close(): Promise<AutosaveResult> {
    // No timer can be left: `flush` cancels the one there is, and once `closing` is set neither a change nor a
    // failure sets another.
    this.closing ??= this.flush().then((result) => {
      this.setStatus('closed');
      return result;
    });
    return this.closing;
  }

  private arm(ms: number): void {
    this.disarm();
    this.cancelTimer = this.options.schedule(ms, () => {
      this.cancelTimer = null;
      void this.drain();
    });
  }

  private disarm(): void {
    this.cancelTimer?.();
    this.cancelTimer = null;
  }

  /** Waits for the save running, then starts one if anything is unsaved (A6: never two at once). */
  private async drain(): Promise<AutosaveResult> {
    while (this.running !== null) await this.running;
    return this.dirty ? this.start() : this.last;
  }

  private start(): Promise<AutosaveResult> {
    this.disarm();
    this.dirty = false;
    const text = this.options.read();
    this.setStatus('saving');
    const running = this.options.save(text).then(
      (result) => this.finish(result),
      () => this.finish(FAILED),
    );
    this.running = running;
    return running;
  }

  private finish(result: AutosaveResult): AutosaveResult {
    this.running = null;
    this.last = result;
    if (result.kind === 'failed') {
      // A7: the text is still unsaved; try again after a while, or once typing pauses before that.
      this.dirty = true;
      this.deadline = this.options.now() + RETRY_MS;
      if (this.closing === null && this.cancelTimer === null) this.arm(RETRY_MS);
      this.setStatus('failed');
    } else {
      this.setStatus(this.dirty ? 'unsaved' : 'saved');
    }
    return result;
  }

  private setStatus(status: AutosaveStatus): void {
    if (status === this.current) return;
    this.current = status;
    this.options.onStatus?.(status);
  }
}
